import type { SeriesStyle } from './seriesStyles'
import {
  SERIES_COLORS,
  SERIES_STYLES,
  GAS_STYLES,
  REDUCTION_STYLES,
  GREENPOWER_STYLES,
} from './seriesStyles'

// Chart palette = the shared series vocabulary (seriesStyles.ts). The generic
// blue ramp is kept as `allColors` for rank-by-colour callers (facility/location
// breakdowns, hashed label colours) that aren't tied to a named domain.
export const allColors: string[] = SERIES_COLORS

// Known categories pin to a fixed DOMAIN style (colour + texture) so a category
// keeps the same look on every chart in every app. Hue identifies the domain:
// reductions = brand blue (dashboards; the detail chart keeps green), green-power =
// amber, gases = three distinct hues (CO₂ blue, CH₄ amber, N₂O green); deal-status stages stay
// on the generic blue ramp. See
// docs/superpowers/specs/2026-07-17-tellus-color-system-design.md.
export const SEMANTIC_STYLES: Record<string, SeriesStyle> = {
  // Reductions (environmental commodity types) - brand blue on the dashboards.
  rec: REDUCTION_STYLES[0],
  srec: REDUCTION_STYLES[1],
  ver: REDUCTION_STYLES[2],
  // Green-power technology types - amber (solar is the hero / first slot).
  solar: GREENPOWER_STYLES[0],
  wind: GREENPOWER_STYLES[1],
  geothermal: GREENPOWER_STYLES[2],
  // Greenhouse gases - three distinct hues (CO₂ blue, N₂O green, CH₄ amber).
  co2: GAS_STYLES[0],
  n2o: GAS_STYLES[1],
  ch4: GAS_STYLES[2],
  // Deal / listing status stages - generic blue ramp (not a data domain).
  active: SERIES_STYLES[0],
  match: SERIES_STYLES[1],
  complete: SERIES_STYLES[2],
}

// The single card hue for a domain. Every member of a bounded domain maps to its
// domain's HERO shade (index 0), so a single-colour card (stat/bars variant) looks
// the same whichever members it happens to contain: every reductions card is the
// brand blue, every green-power card the hero amber, every gas card the core blue -
// never a lighter shade just because a card leads with SREC/VER instead of REC.
// (composition / mixed-unit / legend still use the per-member SEMANTIC_STYLES so
// stacked segments stay distinguishable.)
export const DOMAIN_HERO: Record<string, SeriesStyle> = {
  rec: REDUCTION_STYLES[0], srec: REDUCTION_STYLES[0], ver: REDUCTION_STYLES[0],
  solar: GREENPOWER_STYLES[0], wind: GREENPOWER_STYLES[0], geothermal: GREENPOWER_STYLES[0],
  co2: GAS_STYLES[0], n2o: GAS_STYLES[0], ch4: GAS_STYLES[0],
  active: SERIES_STYLES[0], match: SERIES_STYLES[0], complete: SERIES_STYLES[0],
}

// Canonical display order for known categories, so a domain always lists its
// members in the same sequence (REC, SREC, VER; Solar, Wind, Geothermal) on every
// chart - never re-sorted by value. Indices are per-domain; a chart holds one
// domain, so the overlap across domains is harmless. Unknown categories
// (facilities, locations) are not listed here and fall back to value ranking.
export const SEMANTIC_ORDER: Record<string, number> = {
  rec: 0, srec: 1, ver: 2,
  solar: 0, wind: 1, geothermal: 2,
  co2: 0, n2o: 1, ch4: 2,
  active: 0, match: 1, complete: 2,
}

// The pinned semantic colours, resolved from the domain styles. Consumed by the
// per-app stats services (carbon/ppa) to pre-colour breakdown data.
export const statsColors = {
  status: {
    active: SERIES_STYLES[0].color,
    match: SERIES_STYLES[1].color,
    complete: SERIES_STYLES[2].color,
  },
  techs: {
    wind: GREENPOWER_STYLES[1].color,
    solar: GREENPOWER_STYLES[0].color,
    geothermal: GREENPOWER_STYLES[2].color,
  },
  envCommodityTypes: {
    rec: REDUCTION_STYLES[0].color,
    srec: REDUCTION_STYLES[1].color,
    ver: REDUCTION_STYLES[2].color,
  },
} as const

export default allColors
